import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Undo2, Package, User, Loader2 } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { devolverEquipo } from '@/services/api/inventario.api';
import { getApiErrorMessage } from '@/services/api/client';
import { cn } from '@/lib/utils';

export interface DevolverableItem {
  id_item_maestro: string;
  serial_global: string;
  tipo: string | null;
  empleado_asignado: { nombres: string; documento: string } | null;
}

interface Props {
  item: DevolverableItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DevolverEquipoDialog({ item, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [observaciones, setObservaciones] = useState('');

  function handleClose(open: boolean) {
    if (!open) setObservaciones('');
    onOpenChange(open);
  }

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      devolverEquipo(item!.id_item_maestro, { observaciones: observaciones.trim() || undefined }),
    onSuccess: () => {
      toast.success('Activo devuelto correctamente', {
        description: `${item!.serial_global} volvió a bodega.`,
      });
      void qc.invalidateQueries({ queryKey: ['inventario-items'] });
      void qc.invalidateQueries({ queryKey: ['inventario-dashboard'] });
      handleClose(false);
    },
    onError: (err) => {
      toast.error('No se pudo procesar la devolución', {
        description: getApiErrorMessage(err),
      });
    },
  });

  if (!item) return null;

  const empleadoLabel = item.empleado_asignado?.nombres ?? 'Empleado desconocido';
  const tipoLabel = item.tipo ?? 'Activo';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5 text-amber-600" />
            Devolver activo
          </DialogTitle>
          <DialogDescription>
            El activo volverá a bodega y quedará disponible para reasignarse.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Asset info */}
          <div className="rounded-xl border bg-muted/30 px-4 py-3 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Package className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-mono font-semibold text-foreground">{item.serial_global}</span>
              {item.tipo && (
                <span className="text-muted-foreground">· {tipoLabel}</span>
              )}
            </div>
            {item.empleado_asignado && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4 shrink-0" />
                <span>
                  Asignado a <span className="font-medium text-foreground">{empleadoLabel}</span>
                </span>
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div className="space-y-1.5">
            <Label htmlFor="obs-devolucion" className="text-sm font-medium">
              Observaciones <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Textarea
              id="obs-devolucion"
              placeholder="Estado del equipo al recibir, accesorios entregados…"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={3}
              maxLength={1000}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutate()}
            disabled={isPending}
            className={cn(
              'bg-amber-600 hover:bg-amber-700 text-white gap-2',
              isPending && 'opacity-70',
            )}
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Undo2 className="h-4 w-4" />
            )}
            Confirmar devolución
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
